/**
 * The auth context object and its consumer hook, split from AuthContext.tsx so
 * code that only READS the signed-in user (storefront cart, folder browser)
 * does not import the provider and, through it, the Matrix SDK and the app store.
 */
import { createContext, useContext } from 'react';
import type { User } from '../services/authService';

export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isMatrixConnected: boolean;
  login: (username: string, password: string, homeserver?: string) => Promise<{ success: boolean; error?: string }>;
  register: (username: string, password: string, email: string, subscribeNewsletter: boolean) => Promise<{ success: boolean; pendingVerification?: boolean; error?: string }>;
  verifyEmail: (token: string) => Promise<{ success: boolean; error?: string }>;
  loginWithPasskey: () => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Safe default for when context is unavailable (e.g. during Vite HMR remounts)
const authFallback: AuthContextType = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
  isAdmin: false,
  isMatrixConnected: false,
  login: async () => ({ success: false, error: 'Auth not ready' }),
  register: async () => ({ success: false, error: 'Auth not ready' }),
  verifyEmail: async () => ({ success: false, error: 'Auth not ready' }),
  loginWithPasskey: async () => ({ success: false, error: 'Auth not ready' }),
  logout: async () => {},
  refreshUser: async () => {},
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Return safe fallback instead of throwing — prevents white screen during HMR
    // Expected on the lean public storefront until its auth layer loads; only worth a warning in dev.
    if (import.meta.env.DEV) console.warn('useAuth called outside AuthProvider (likely HMR remount)');
    return authFallback;
  }
  return context;
};
